import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getSystemCanvasControlTypes, getSystemCanvasControlTypeById } from '../../Pages/Library/CustomModule/Store/CustomModule.action';
import { AddIcon, CustomModulesIcon, LibraryIcon } from '../../bombiix-icon';
import { NewInput } from '../PIM/common/NewInput';
import type { LibraryTitleInputProps } from './library-title-input.types';
import useSaveToLibrary from './use-save-to-library';
import LibraryTitleAppearanceContext from './library-title-input.context';

const LibraryTitleInput: React.FC<LibraryTitleInputProps> = ({
  value,
  onChange,
  onSave,
  onSelect,
  controlType,
  contentData,
  placeholder,
  className = '',
  debounceDelay = 500,
  appearance = 'default',
}) => {
  const { t } = useTranslation('custom-module-system-canvas');
  const dispatch = useDispatch();
  const saveToLibrary = useSaveToLibrary();
  const contextAppearance = useContext(LibraryTitleAppearanceContext);
  const visualAppearance = appearance === 'default' ? contextAppearance : appearance;

  const [controlOptions, setControlOptions] = useState<Array<{ _id: string; title?: string }>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const query = value?.trim() || '';

    if (!query) {
      if (isMountedRef.current) {
        setControlOptions([]);
        setIsSearching(false);
        setHasSearched(false);
      }
      return;
    }

    setIsSearching(true);
    setHasSearched(false);

    debounceTimerRef.current = setTimeout(async () => {
      try {
        const response = (await dispatch(
          getSystemCanvasControlTypes({
            controltype: controlType,
            search: query,
          }),
        )) as any;

        if (isMountedRef.current) {
          const controlTypes = response?.data?.controls || response?.data || [];
          setControlOptions(Array.isArray(controlTypes) ? controlTypes : []);
          setIsSearching(false);
          setHasSearched(true);
        }
      } catch (error) {
        if (isMountedRef.current) {
          setControlOptions([]);
          setIsSearching(false);
          setHasSearched(true);
        }
      }
    }, debounceDelay);
  }, [dispatch, value, controlType, debounceDelay]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.value);
    },
    [onChange],
  );

  const handleSave = useCallback(async () => {
    const title = value?.trim() || '';
    const isSaved = await saveToLibrary(title, controlType, contentData);
    if (isSaved) {
      onSave?.(title);
    }
  }, [value, contentData, controlType, saveToLibrary, onSave]);

  const handleOptionSelect = useCallback(
    async (option: { _id: string; title?: string }) => {
      if (!option?._id) return;

      setControlOptions([]);
      setHasSearched(false);

      try {
        const detailResponse = (await dispatch(getSystemCanvasControlTypeById(option._id))) as any;
        const controlTypeData = detailResponse?.data?.controlType ?? detailResponse?.data;
        onSelect?.(controlTypeData);
        if (appearance === 'default') {
          onChange?.('');
        }
      } catch (error) {
        console.error('Failed to load control detail:', error);
      }
    },
    [dispatch, onChange, onSelect, appearance],
  );

  const showErrorMessage = React.useMemo(
    () => appearance === 'default' && !!(value?.trim() && hasSearched && !isSearching && controlOptions.length === 0),
    [appearance, value, hasSearched, isSearching, controlOptions.length],
  );

  const controlTypeLabel = React.useMemo(() => {
    if (!showErrorMessage) return '';
    const controlTypeMap: Record<string, string> = {
      section: t('COMMON.SECTION'),
      subSection: t('COMMON.SUB_SECTION'),
      subsection: t('COMMON.SUB_SECTION'),
      field: t('COMMON.FIELD'),
      dropdown: t('COMMON.FIELD_TYPES.DROPDOWN'),
      dorpdown: t('COMMON.FIELD_TYPES.DROPDOWN'),
      radio: t('COMMON.FIELD_TYPES.RADIO_BUTTON'),
      checkbox: t('COMMON.FIELD_TYPES.CHECKBOX'),
      text: t('COMMON.FIELD_TYPES.TEXT_BOX'),
      'date-picker': t('COMMON.FIELD_TYPES.DATE_PICKER'),
      date: t('COMMON.FIELD_TYPES.DATE_PICKER'),
      'time-picker': t('COMMON.FIELD_TYPES.TIME_PICKER'),
      comment: t('COMMON.FIELD_TYPES.COMMENT'),
      status: t('COMMON.FIELD_TYPES.STATUS'),
      source: t('COMMON.FIELD_TYPES.SOURCE'),
      table: t('COMMON.TABLE'),
      image: t('COMMON.FIELD_TYPES.IMAGE'),
    };
    return controlTypeMap[controlType] || controlType;
  }, [controlType, t, showErrorMessage]);

  const handleAddKeyDown = (event: React.KeyboardEvent<SVGSVGElement>) => {
    if (ACTIVATION_KEYS.includes(event.key)) {
      event.preventDefault();
      handleSave();
    }
  };

  const renderEndAdornment = () =>
    visualAppearance === 'inline' ? (
      <>
        <span className="cm-inline-library__divider" aria-hidden="true" />
        <button type="button" className="cm-inline-library__button" aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.SAVE_TO_LIBRARY')} onClick={handleSave}>
          <LibraryIcon size={INLINE_LIBRARY_ICON_SIZE} />
        </button>
      </>
    ) : (
      <AddIcon
        size={24}
        className="cursor-pointer cm-edit-icon"
        role="button"
        tabIndex={0}
        aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.SAVE_TO_LIBRARY')}
        onClick={handleSave}
        onKeyDown={handleAddKeyDown}
      />
    );

  return (
    <>
      <div
        className={`${visualAppearance === 'default' ? 'mb-2' : 'cm-library-title-input--inline'} cm-library-title-input ${className}`}
        style={{ position: 'relative' }}
      >
        <NewInput
          placeholder={placeholder || t('CUSTOM_MODULE.SYSTEM_CANVAS.ENTER_LIBRARY_TITLE')}
          value={value || ''}
          onChange={handleInputChange}
          {...(visualAppearance === 'default' ? { startAdornment: <CustomModulesIcon size={24} /> } : {})}
          endAdornment={renderEndAdornment()}
        />
        {controlOptions.length > 0 && (
          <div className="suggestion-dropdown">
            {controlOptions.map((option) => (
              <p className="suggestion-dropdown-item m-0 mx-2 cursor-pointer w-90" key={option._id} onClick={() => handleOptionSelect(option)}>
                {option.title || option._id}
              </p>
            ))}
          </div>
        )}
      </div>
      {showErrorMessage && (
        <div className="text-danger f-12 m-0">{t('CUSTOM_MODULE.SYSTEM_CANVAS.CONTROL_WITH_THIS_NAME_NOT_FOUND', { controlType: controlTypeLabel })}</div>
      )}
    </>
  );
};

export default LibraryTitleInput;

const INLINE_LIBRARY_ICON_SIZE = 20;

const ACTIVATION_KEYS = ['Enter', ' '];
